import React, { useState } from "react"
import { useSnackbar } from "notistack"
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import i18n from "../../i18n"
import {
  useContacts,
  useCreateContact,
  useCompanies,
  useContactTags,
  useDeleteContact,
  useEditContact,
} from "./hooks/useContacts"
import ContactsHeader from "./ContactsHeader"
import ContactsGrid from "./ContactsGrid"
import ContactsCards from "./ContactsCards"
import EmptyState from "../../common/EmptyState"
import { GRID_VIEW, CARD_VIEW } from "./constants"
import useLocalStorage from "../../hooks/useLocalStorage"
import ContactModal from "./ContactModal"
import ImportContactsModal from "./ImportContactsModal"
import { useAuthContext } from "../../context/auth/authContext"

const Contacts = () => {
  const { user } = useAuthContext()
  const { enqueueSnackbar } = useSnackbar()

  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const organizationId = user["custom:organizationId"]

  const { data: contacts, isLoading, error } = useContacts(organizationId)
  const createContactMutation = useCreateContact()
  const editContactMutation = useEditContact()
  const deleteContactMutation = useDeleteContact()
  const { data: companies } = useCompanies(organizationId)
  const { data: contactTags } = useContactTags(organizationId)
  const queryClient = useQueryClient()
  const [viewMode, setViewMode] = useLocalStorage("contactsViewMode", GRID_VIEW)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [contactToDelete, setContactToDelete] = useState(null)
  const [importModalOpen, setImportModalOpen] = useState(false)

  const handleAddNewContact = () => {
    setEditingContact(null)
    setModalOpen(true)
  }

  const handleOpenImport = () => {
    setImportModalOpen(true)
  }

  const handleCloseImport = () => {
    setImportModalOpen(false)
  }

  const handleImported = () => {
    queryClient.invalidateQueries({ queryKey: ["contacts"] })
    queryClient.invalidateQueries({ queryKey: ["companies"] })
  }

  const handleEditContact = contact => {
    setEditingContact(contact)
    setModalOpen(true)
  }

  const handleDeleteContact = contact => {
    setContactToDelete(contact)
    setDeleteModalOpen(true)
  }

  const handleViewModeChange = newViewMode => {
    setViewMode(newViewMode)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingContact(null)
  }

  const handleSaveContact = (contactData, companyId) => {
    if (editingContact) {
      // Actualizar contacto
      editContactMutation.mutate(
        { contactData, companyId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] })
            setModalOpen(false)
            setEditingContact(null)
          },
          onError: error => {
            console.error("Error al actualizar contacto:", error)
            enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyUpdated"), {
              variant: "error",
            })
          },
        },
      )
    } else {
      // Crear nuevo contacto
      createContactMutation.mutate(
        { organizationId, contactData, companyId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] })
            setModalOpen(false)
            setEditingContact(null)
          },
          onError: error => {
            console.error("Error al crear contacto:", error)
            enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyCreated"), {
              variant: "error",
            })
          },
        },
      )
    }
  }

  const handleConfirmDelete = () => {
    if (contactToDelete) {
      deleteContactMutation.mutate(
        {
          organizationId,
          contactId: contactToDelete.SK,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] })
            setDeleteModalOpen(false)
            setContactToDelete(null)
          },
          onError: error => {
            console.error("Error al eliminar contacto:", error)
            enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyDeleted"), {
              variant: "error",
            })
          },
        },
      )
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setContactToDelete(null)
  }

  return (
    <Box sx={{ paddingInline: "30px" }} data-testid="contacts-container">
      <ContactsHeader
        onAddNewContact={handleAddNewContact}
        onOpenImport={handleOpenImport}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ textAlign: "center", py: 4, color: "error.main" }}>
          {i18n.t("contacts.errorLoadingContacts")}: {error.message}
        </Box>
      )}

      {!isLoading &&
        !error &&
        (contacts && contacts.length > 0 ? (
          viewMode === CARD_VIEW ? (
            <ContactsGrid
              contacts={contacts}
              onEditContact={handleEditContact}
              onDeleteContact={handleDeleteContact}
              companies={companies}
            />
          ) : (
            <ContactsCards
              contacts={contacts}
              onEditContact={handleEditContact}
              onDeleteContact={handleDeleteContact}
            />
          )
        ) : (
          <EmptyState
            title={i18n.t("contacts.emptyStateMessage")}
            buttonText={i18n.t("contacts.addNewContact")}
            onButtonClick={handleAddNewContact}
            secondaryButtonText={i18n.t("contacts.importFromExcel")}
            onSecondaryButtonClick={handleOpenImport}
          />
        ))}

      <ContactModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveContact}
        contact={editingContact}
        companies={companies}
        contactTags={contactTags}
        organizationId={organizationId}
        queryClient={queryClient}
      />

      <ImportContactsModal
        open={importModalOpen}
        onClose={handleCloseImport}
        organizationId={organizationId}
        onImported={handleImported}
      />

      <Dialog open={deleteModalOpen} onClose={handleCancelDelete}>
        <DialogTitle>{i18n.t("contacts.confirmDelete.title")}</DialogTitle>
        <DialogContent>
          {i18n.t("contacts.confirmDelete.message")}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {i18n.t("contacts.confirmDelete.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Contacts
